import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Button, Paper, TextField, Stack, CircularProgress,
  Alert, Grid, Switch, FormControlLabel, Divider, Avatar, alpha,
  IconButton, Tooltip, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WalletIcon from '@mui/icons-material/Wallet';
import {
  fetchDesignPlan, createDesignPlan, updateDesignPlan,
  fetchPlanWallets, assignWalletToPlan, unassignWalletFromPlan,
  fetchAllWallets
} from '../designApi';

const CURRENCIES = [
  { code: 'YER', name: 'Yemeni Rial', symbol: '﷼' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' }
];

export default function AddEditDesignPlan() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isEdit = Boolean(id);

  // Plan form state
  const [form, setForm] = useState({
    Name: '',
    Description: '',
    NumberOfDesigns: '',
    Price: '',
    Currency: 'YER',
    IsActive: true
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Wallet assignment state
  const [planWallets, setPlanWallets] = useState([]);
  const [allWallets, setAllWallets] = useState([]);
  const [walletDialog, setWalletDialog] = useState(false);
  const [walletForm, setWalletForm] = useState({
    WalletId: '',
    WalletNumber: '',
    WalletAccountName: ''
  });
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [walletSaving, setWalletSaving] = useState(false);

  // Load plan data if editing
  useEffect(() => {
    if (isEdit) {
      loadPlan();
      loadPlanWallets();
    }
    loadAllWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const data = await fetchDesignPlan(id);
      setForm({
        Name: data.Name || '',
        Description: data.Description || '',
        NumberOfDesigns: data.NumberOfDesigns || '',
        Price: data.Price || '',
        Currency: data.Currency || 'YER',
        IsActive: data.IsActive !== false
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load plan');
    } finally {
      setLoading(false);
    }
  };

  const loadPlanWallets = async () => {
    try {
      setWalletsLoading(true);
      const data = await fetchPlanWallets(id);
      console.log('📦 fetchPlanWallets response:', data);

      // ✅ Handle all possible response structures:
      // Backend might return: [...] OR { wallets: [...] } OR { data: [...] }
      let wallets = [];
      if (Array.isArray(data)) {
        wallets = data;
      } else if (data && Array.isArray(data.wallets)) {
        wallets = data.wallets;
      } else if (data && Array.isArray(data.data)) {
        wallets = data.data;
      }

      setPlanWallets(wallets);
    } catch (err) {
      console.error('Failed to load plan wallets:', err);
      setPlanWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  };

  const loadAllWallets = async () => {
    try {
      const data = await fetchAllWallets();
      console.log('📦 fetchAllWallets response:', data);

      // ✅ Handle all possible response structures
      let wallets = [];
      if (Array.isArray(data)) {
        wallets = data;
      } else if (data && Array.isArray(data.wallets)) {
        wallets = data.wallets;
      } else if (data && Array.isArray(data.data)) {
        wallets = data.data;
      } else if (data && Array.isArray(data.rows)) {
        wallets = data.rows;
      }

      setAllWallets(wallets);
    } catch (err) {
      console.error('Failed to load wallets:', err);
      setAllWallets([]);
    }
  };

  const handleChange = (field) => (e) => {
    const value = field === 'IsActive' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.Name.trim()) {
      setError('Plan name is required');
      return;
    }
    if (!form.NumberOfDesigns || parseInt(form.NumberOfDesigns) <= 0) {
      setError('Number of designs must be greater than 0');
      return;
    }
    if (!form.Price || parseFloat(form.Price) <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        Name: form.Name.trim(),
        Description: form.Description.trim(),
        NumberOfDesigns: parseInt(form.NumberOfDesigns),
        Price: parseFloat(form.Price),
        Currency: form.Currency,
        IsActive: form.IsActive
      };

      if (isEdit) {
        await updateDesignPlan(id, payload);
        setSuccess('Plan updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const result = await createDesignPlan(payload);
        console.log('✅ Create plan response:', result);
        setSuccess('Plan created successfully');

        // ✅ Safely extract the plan ID from various possible response structures
        const newPlanId =
          result?.plan?.Id ||
          result?.plan?.id ||
          result?.Id ||
          result?.id ||
          result?.data?.plan?.Id ||
          result?.data?.plan?.id ||
          result?.data?.Id ||
          result?.data?.id ||
          null;

        if (newPlanId) {
          setTimeout(() => {
            navigate(`/admin/design-plans/edit/${newPlanId}`);
          }, 1000);
        } else {
          console.warn('⚠️ Could not extract plan ID from response:', result);
          setTimeout(() => {
            navigate('/admin/design-plans');
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignWallet = async () => {
    if (!walletForm.WalletId) {
      setError('Please select a wallet');
      return;
    }
    if (!walletForm.WalletNumber.trim()) {
      setError('Wallet number is required');
      return;
    }

    try {
      setError('');
      setWalletSaving(true);

      // ✅ Send both PascalCase and camelCase to cover any backend expectation
      const payload = {
        WalletId: walletForm.WalletId,
        walletId: walletForm.WalletId,
        WalletNumber: walletForm.WalletNumber.trim(),
        walletNumber: walletForm.WalletNumber.trim(),
        WalletAccountName: walletForm.WalletAccountName?.trim() || null,
        walletAccountName: walletForm.WalletAccountName?.trim() || null
      };

      console.log('🟡 Assigning wallet - Plan ID:', id, 'Payload:', payload);
      const result = await assignWalletToPlan(id, payload);
      console.log('✅ Assign wallet response:', result);

      setWalletDialog(false);
      setWalletForm({ WalletId: '', WalletNumber: '', WalletAccountName: '' });

      // ✅ Await the reload so wallets show immediately
      await loadPlanWallets();

      setSuccess('Wallet assigned successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Assign wallet error:', err);
      console.error('❌ Error response:', err.response?.data);
      setError(err.response?.data?.message || err.message || 'Failed to assign wallet');
    } finally {
      setWalletSaving(false);
    }
  };

  const handleRemoveWallet = async (walletId) => {
    if (!walletId) {
      console.error('No walletId provided for removal');
      return;
    }

    try {
      setError('');
      console.log('🗑️ Removing wallet - Plan ID:', id, 'Wallet ID:', walletId);
      await unassignWalletFromPlan(id, walletId);

      // ✅ Await the reload
      await loadPlanWallets();

      setSuccess('Wallet removed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Remove wallet error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to remove wallet');
    }
  };

  // ✅ Filter out already assigned wallets (handle both Id and id)
  const availableWallets = allWallets.filter(w => {
    const wId = w.Id || w.id;
    return !planWallets.some(pw => {
      const pwWalletId = pw.WalletId || pw.walletId;
      return pwWalletId === wId;
    });
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#ad1457', 0.08)} 0%, ${alpha('#6a1b9a', 0.08)} 100%)`,
          border: `1px solid ${alpha('#ad1457', 0.15)}`
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => navigate('/admin/design-plans')} sx={{ bgcolor: alpha('#ad1457', 0.1) }}>
            <ArrowBackIcon sx={{ color: '#ad1457' }} />
          </IconButton>
          <Avatar sx={{ bgcolor: '#ad1457', width: 50, height: 50 }}>
            <DesignServicesIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#ad1457">
              {isEdit ? (t('EditPlan') || 'Edit Plan') : (t('AddPlan') || 'Add Plan')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit ? (t('EditPlanDesc') || 'Update plan details and manage wallets') : (t('AddPlanDesc') || 'Create a new design subscription plan')}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Plan Form */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3, border: `1px solid ${alpha('#000', 0.08)}` }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {t('PlanDetails') || 'Plan Details'}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('PlanName') || 'Plan Name'}
              value={form.Name}
              onChange={handleChange('Name')}
              required
              placeholder="e.g. Basic Design Package"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('Description') || 'Description'}
              value={form.Description}
              onChange={handleChange('Description')}
              multiline
              rows={3}
              placeholder="Describe what this plan includes..."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t('NumberOfDesigns') || 'Number of Designs'}
              value={form.NumberOfDesigns}
              onChange={handleChange('NumberOfDesigns')}
              type="number"
              required
              inputProps={{ min: 1 }}
              placeholder="e.g. 10"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t('Price') || 'Price'}
              value={form.Price}
              onChange={handleChange('Price')}
              type="number"
              required
              inputProps={{ min: 0, step: 0.01 }}
              placeholder="e.g. 5000"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('Currency') || 'Currency'}</InputLabel>
              <Select
                value={form.Currency}
                onChange={handleChange('Currency')}
                label={t('Currency') || 'Currency'}
              >
                {CURRENCIES.map(c => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.symbol} {c.code} - {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.IsActive}
                  onChange={handleChange('IsActive')}
                  color="success"
                />
              }
              label={t('ActivePlan') || 'Active (visible to admins)'}
            />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/design-plans')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {t('Cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: '#ad1457',
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              '&:hover': { bgcolor: '#880e4f' }
            }}
          >
            {saving
              ? (t('Saving') || 'Saving...')
              : (isEdit
                ? (t('UpdatePlan') || 'Update Plan')
                : (t('CreatePlan') || 'Create Plan')
              )
            }
          </Button>
        </Stack>
      </Paper>

      {/* Wallet Assignment Section (Only in Edit mode) */}
      {isEdit && (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: `1px solid ${alpha('#000', 0.08)}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: alpha('#1565c0', 0.1), width: 40, height: 40 }}>
                <AccountBalanceWalletIcon sx={{ color: '#1565c0', fontSize: 22 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {t('AssignedWallets') || 'Assigned Wallets'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('AssignedWalletsDesc') || 'Payment wallets customers can use to pay for this plan'}
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setWalletDialog(true)}
              disabled={availableWallets.length === 0}
              sx={{
                textTransform: 'none',
                borderColor: '#1565c0',
                color: '#1565c0',
                borderRadius: 2,
                '&:hover': { borderColor: '#0d47a1', bgcolor: alpha('#1565c0', 0.05) }
              }}
            >
              {t('AssignWallet') || 'Assign Wallet'}
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {walletsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={30} />
            </Box>
          ) : planWallets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: alpha('#000', 0.02), borderRadius: 2 }}>
              <WalletIcon sx={{ fontSize: 48, color: alpha('#000', 0.15), mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('NoWalletsAssigned') || 'No wallets assigned to this plan yet'}
              </Typography>
              {allWallets.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  No wallets found in the system. Please create wallets first.
                </Typography>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#1565c0', 0.05) }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t('WalletName') || 'Wallet Name'}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('WalletNumber') || 'Wallet Number'}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('AccountName') || 'Account Name'}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">{t('Actions') || 'Actions'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {planWallets.map((pw, index) => {
                    // ✅ Handle different field name formats from backend
                    const walletId = pw.WalletId || pw.walletId;
                    const walletName = pw.WalletName || pw.walletName || pw.Wallet?.Name || pw.wallet?.Name || '-';
                    const walletNumber = pw.WalletNumber || pw.walletNumber || '-';
                    const walletAccountName = pw.WalletAccountName || pw.walletAccountName || '-';
                    const walletImage = pw.WalletImage || pw.walletImage || pw.Wallet?.Image || pw.Wallet?.ImageUrl;

                    return (
                      <TableRow key={pw.Id || pw.id || walletId || index} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {walletImage && (
                              <Avatar src={walletImage} sx={{ width: 28, height: 28 }} />
                            )}
                            <Typography variant="body2" fontWeight={600}>
                              {walletName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip label={walletNumber} size="small" sx={{ fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {walletAccountName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t('Remove') || 'Remove'}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveWallet(walletId)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Debug info - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: 11 }}>
              <Typography variant="caption" color="text.secondary">
                Debug: {planWallets.length} plan wallets | {allWallets.length} total wallets | {availableWallets.length} available
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Assign Wallet Dialog */}
      <Dialog
        open={walletDialog}
        onClose={() => {
          if (!walletSaving) {
            setWalletDialog(false);
            setWalletForm({ WalletId: '', WalletNumber: '', WalletAccountName: '' });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('AssignWallet') || 'Assign Wallet to Plan'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {availableWallets.length === 0 ? (
              <Alert severity="info">
                No available wallets to assign. All wallets are already assigned to this plan.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth required>
                  <InputLabel>{t('SelectWallet') || 'Select Wallet'}</InputLabel>
                  <Select
                    value={walletForm.WalletId}
                    onChange={(e) => setWalletForm(prev => ({ ...prev, WalletId: e.target.value }))}
                    label={t('SelectWallet') || 'Select Wallet'}
                    disabled={walletSaving}
                  >
                    {availableWallets.map(w => {
                      // ✅ Handle both Id/id and Image/ImageUrl field names
                      const wId = w.Id || w.id;
                      const wName = w.Name || w.name;
                      const wImage = w.ImageUrl || w.imageUrl || w.Image || w.image;

                      return (
                        <MenuItem key={wId} value={wId}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {wImage && (
                              <Avatar src={wImage} sx={{ width: 24, height: 24 }} />
                            )}
                            <span>{wName}</span>
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={t('WalletNumber') || 'Wallet Number'}
                  value={walletForm.WalletNumber}
                  onChange={(e) => setWalletForm(prev => ({ ...prev, WalletNumber: e.target.value }))}
                  required
                  disabled={walletSaving}
                  placeholder="e.g. 777123456"
                />
                <TextField
                  fullWidth
                  label={t('AccountName') || 'Account Name (Optional)'}
                  value={walletForm.WalletAccountName}
                  onChange={(e) => setWalletForm(prev => ({ ...prev, WalletAccountName: e.target.value }))}
                  disabled={walletSaving}
                  placeholder="e.g. StoresYe Designs"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setWalletDialog(false);
              setWalletForm({ WalletId: '', WalletNumber: '', WalletAccountName: '' });
            }}
            disabled={walletSaving}
            sx={{ textTransform: 'none' }}
          >
            {t('Cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignWallet}
            disabled={walletSaving || !walletForm.WalletId || !walletForm.WalletNumber.trim()}
            startIcon={walletSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
          >
            {walletSaving ? (t('Assigning') || 'Assigning...') : (t('Assign') || 'Assign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}